import {
    AfterContentInit,
    DestroyRef,
    Directive,
    ElementRef,
    OnInit,
    TemplateRef,
    inject,
    input,
    output,
    contentChild,
    contentChildren,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Subject, debounceTime, delay, filter, fromEvent, map, merge, skip, startWith, takeUntil, tap } from 'rxjs';

@Directive({ selector: '[appTopBarUnitTree]' })
export class TopBarUnitTreeDirective implements OnInit, AfterContentInit {
    #el = inject(ElementRef);
    #destroyRef = inject(DestroyRef);

    destroyed$ = new Subject<void>();
    readonly gapWidths = input(20);
    readonly hiddenElements = output<string[]>();
    readonly goUpButton = contentChild<TemplateRef<any>>('goUpButton');
    readonly unitHierarchyItems = contentChildren<ElementRef<HTMLElement>>('unitHierarchyItem');
    readonly #unitHierarchyItems$ = toObservable(this.unitHierarchyItems).pipe(startWith(this.unitHierarchyItems()));

    #hiddenElementsMap: Map<HTMLElement, number> = new Map();
    unitItemChanges$: Subject<readonly ElementRef<HTMLElement>[]> = new Subject();
    domManipulated$ = new Subject<void>();
    windowResize$ = fromEvent(window, 'resize').pipe(takeUntilDestroyed(), debounceTime(50));

    eventListeners$ = merge(
        this.unitItemChanges$.pipe(
            tap(() => {
                this.#hiddenElementsMap.clear();
                this.hiddenElements.emit([]);
            }),
            delay(10),
        ),
        this.windowResize$,
        this.domManipulated$.pipe(
            tap(() => {
                const hiddenElementUuids = Array.from(this.#hiddenElementsMap.keys()).map((el) => el.dataset.uuid);
                this.hiddenElements.emit(hiddenElementUuids);
            }),
        ),
    );

    ngOnInit() {
        this.#destroyRef.onDestroy(() => {
            this.destroyed$.next();
            this.destroyed$.complete();
        });

        this.eventListeners$
            .pipe(
                takeUntil(this.destroyed$),
                filter(() => true),
                map((_) => [this.#el, this.unitHierarchyItems()]),
                tap(([unitTreeElement, items]: [ElementRef<HTMLElement>, ElementRef<HTMLElement>[]]) => {
                    const containerWidth = unitTreeElement.nativeElement.offsetWidth;
                    const childrenArray: HTMLElement[] = [].slice.call(unitTreeElement.nativeElement.children);
                    const treeWidth = childrenArray.reduce((acc, child) => acc + child.offsetWidth, 0);

                    if (containerWidth < treeWidth + this.gapWidths()) {
                        const firstVisibleItem = items.find((item) => item.nativeElement.offsetLeft > 0);
                        if (firstVisibleItem) {
                            this.#hiddenElementsMap.set(firstVisibleItem.nativeElement, firstVisibleItem.nativeElement.offsetWidth);
                            firstVisibleItem.nativeElement.style.display = 'none';
                            this.domManipulated$.next();
                        }
                    } else {
                        const hiddenItems = items.filter((item) => item.nativeElement.style.display === 'none');
                        const lastHiddenItem = hiddenItems[hiddenItems.length - 1];

                        if (lastHiddenItem) {
                            const widthOflastHiddenItem = this.#hiddenElementsMap.get(lastHiddenItem.nativeElement);

                            const showItem = containerWidth > treeWidth + widthOflastHiddenItem + this.gapWidths();
                            if (showItem) {
                                lastHiddenItem.nativeElement.style.display = '';
                                this.#hiddenElementsMap.delete(lastHiddenItem.nativeElement);
                                this.domManipulated$.next();
                            }
                        }
                    }
                }),
            )
            .subscribe();
    }

    ngAfterContentInit() {
        this.#unitHierarchyItems$
            .pipe(
                takeUntil(this.destroyed$),
                skip(1),
                tap((items: readonly ElementRef<HTMLElement>[]) => {
                    this.#hiddenElementsMap.clear();
                    this.unitItemChanges$.next(items);
                }),
            )
            .subscribe();
    }
}
